import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_QUOTE_REQUESTS } from "../../../../utils/GraphQL/queries";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrRowButton from "../../../../components/BdrDisclosure/BdrRowButton";
import EmailModal from "../EmailModal/EmailModal";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import "./quoteRequestsModal.css";

/**
 * QuoteRequestsModal — the public site's quote requests from the last 7 days.
 *
 * ─────────────────────────────────────────────────────────────────
 * IT DRAWS ITS OWN ROW, and that is the cheap option, not the lazy one.
 * There is no `EmailRow` component in this app — the inbox row is ~130 lines
 * inlined in `Emails.jsx`, reading `selectMode`, `selectedIds`, `bulkBusy`,
 * `getArchived[0]` and `prepareReplyEmail` straight out of page scope. Lifting
 * it out would be unscoped surgery on a 1,100-line live page; copying it would
 * be the duplication this whole console exists to undo. This surface needs a
 * name, a date and a way in — no bulk select, no flags, no archive, no reply.
 *
 * THE LAYER RUNGS ARE NOT DECORATION. This modal sits at `base` and opens
 * `EmailModal` at `raised`, with the image lightbox above that at `top`. Two
 * BdrModals both defaulting to `base` land in the same z-band ordered only by
 * DOM insertion order — which is the exact failure the ladder exists to stop.
 *
 * OPENING A ROW MARKS IT READ for free: `getEmailById` flips `emailRead` as a
 * side effect of the fetch, so there is no extra mutation to wire and the
 * unread dot clears on the next read.
 *
 * NO `prepareReply` IS PASSED, deliberately. There is nowhere here to mount a
 * reply composer, and `EmailModal` now hides the Reply button rather than
 * throwing when the prop is absent. Replying belongs on the Emails page.
 * ─────────────────────────────────────────────────────────────────
 */

/* "New Quote Submission - Jane Doe" -> "Jane Doe". Falls back to the whole
   subject if the shape ever changes, so a row is never blank. */
const nameFromSubject = (subject) => {
  if (!subject) return "(no name)";
  const idx = subject.indexOf(" - ");
  return idx === -1 ? subject : subject.slice(idx + 3).trim() || "(no name)";
};

const QuoteRequestsModal = ({ open, onClose }) => {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [openEmailId, setOpenEmailId] = useState("");
  const [fetchRequests] = useLazyQuery(GET_QUOTE_REQUESTS, {
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    fetchRequests()
      .then(({ data, error }) => {
        if (!alive) return;
        /* `error` off the resolved result — useLazyQuery resolves rather than
           rejects on a GraphQL error, so a `.catch` alone would render a
           failure as "No quote requests in the last 7 days". */
        if (error) {
          setFailed(true);
          setRows([]);
          return;
        }
        setRows(data?.getQuoteRequests?.emails || []);
        setFailed(false);
      })
      .catch(() => {
        if (!alive) return;
        setFailed(true);
        setRows([]);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <>
      <BdrModal
        open
        onClose={onClose}
        variant="panel"
        size="md"
        layer="base"
        title="Quote Requests · last 7 days"
        ariaLabel="Quote requests from the last 7 days"
      >
        {rows === null && !failed ? (
          <ul className="qr-rows">
            {[0, 1, 2].map((i) => (
              <li key={i} className="qr-row qr-row--skeleton" aria-hidden>
                <span className="qr-skeleton-bar" />
              </li>
            ))}
          </ul>
        ) : failed ? (
          <p className="qr-empty">Couldn't load quote requests.</p>
        ) : rows.length === 0 ? (
          <p className="qr-empty">No quote requests in the last 7 days.</p>
        ) : (
          <ul className="qr-rows">
            {rows.map((email) => (
              <li key={email?.id}>
                <BdrRowButton
                  className="qr-row qr-row--btn"
                  onClick={() => setOpenEmailId(email?.id)}
                  label={
                    <span className="qr-main">
                    <span className="qr-name">
                      {!email?.emailRead && (
                        <span className="qr-unread" aria-label="Unread" />
                      )}
                      {nameFromSubject(email?.emailSubject)}
                    </span>
                      <span className="qr-from">{email?.from}</span>
                    </span>
                  }
                  trailing={
                    <span className="qr-date">
                      {BAGroupTimeFormat(email?.dateCreated)}
                    </span>
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </BdrModal>

      {openEmailId && (
        <EmailModal
          emailId={openEmailId}
          closeEmail={() => setOpenEmailId("")}
          layer="raised"
          imageLayer="top"
        />
      )}
    </>
  );
};

export default QuoteRequestsModal;
